import parseHtml from 'html-react-parser';
import katex from 'katex';
import {
  Fragment,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';

import { ModalsManager } from '@/components/modals/modalsManager';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useInViewport } from '@/hooks/useInViewport';

import { StaticExportContext } from '../../static-export-context';
import { simpleHash } from '../../utils';

import {
  getCachedLatex,
  getLatexCacheKey,
  LatexPreviewView,
  renderBlockLatex,
  setCachedLatex,
} from './LatexPreviewView';
import { type StaticCodeProps } from './StaticCode';

const INLINE_LATEX_CACHE_LIMIT = 100;
const inlineLatexCache = new Map<string, ReactNode>();

export function Latex({ children: content }: StaticCodeProps) {
  const segments = useMemo(() => extractSegments(content as string), [content]);

  return segments.map(({ content, type }) => {
    const key = simpleHash(content);
    switch (type) {
      case 'inline':
        return <InlineLatex key={key}>{content}</InlineLatex>;
      case 'block':
        return <BlockLatex key={key}>{content}</BlockLatex>;
      case 'text':
      default:
        return <Fragment key={key}>{content}</Fragment>;
    }
  });
}

export function BlockLatex({ children: content }: StaticCodeProps) {
  const [latexNode, setLatexNode] = useState<ReactNode>(null);
  const copyText = useMemo(
    () => (typeof content === 'string' ? content : ''),
    [content]
  );
  const cacheKey = useMemo(() => getLatexCacheKey(copyText), [copyText]);

  const isStaticExport = useContext(StaticExportContext);
  const { ref, inViewport } = useInViewport({ enabled: !isStaticExport });

  const renderLatex = useDebouncedCallback(
    () => {
      try {
        const latexElement = renderBlockLatex(copyText);
        setLatexNode(latexElement);
        setCachedLatex(copyText, latexElement);
      } catch (_error) {
        //
      }
    },
    {
      delay: 250,
      flushOnUnmount: true,
    }
  );

  useEffect(() => {
    // Only generate block LaTeX when in viewport for performance
    if (!inViewport) return;

    // Check cache first - implement LRU by moving to end
    const cachedLatex = getCachedLatex(copyText);
    if (cachedLatex) {
      setLatexNode(cachedLatex);
      return;
    }

    renderLatex();
  }, [cacheKey, copyText, renderLatex, inViewport]);

  const handleExpand = useCallback(() => {
    void import('@/components/modals/LatexPreviewModal').then((modal) => {
      ModalsManager.show(modal.LatexPreviewModal, {
        content: copyText,
      });
    });
  }, [copyText]);

  return (
    <div
      data-latex-preview="true"
      data-no-copy="true"
      data-render-pending={
        isStaticExport && !latexNode ? 'equation' : undefined
      }
      ref={ref}
      className="h-[12rem] w-full"
    >
      {inViewport && latexNode && (
        <LatexPreviewView
          latexNode={latexNode}
          copyText={copyText}
          onExpand={handleExpand}
        />
      )}
    </div>
  );
}

export function InlineLatex({ children: content }: StaticCodeProps) {
  const isStaticExport = useContext(StaticExportContext);
  const [latexNode, setLatexNode] = useState<ReactNode>(null);

  const cacheKey = useMemo(() => `inline-latex:${content}`, [content]);

  const renderLatex = useDebouncedCallback(
    () => {
      try {
        const html = katex.renderToString(content as string, {
          throwOnError: false,
          displayMode: false,
          strict: false,
          output: 'mathml',
        });
        const latexElement = parseHtml(html);
        setLatexNode(latexElement);
        inlineLatexCache.set(cacheKey, latexElement);
        manageInlineCacheSize();
      } catch (_error) {
        //
      }
    },
    {
      delay: 250,
      flushOnUnmount: true,
    }
  );

  useEffect(() => {
    // Check cache first - implement LRU by moving to end
    if (inlineLatexCache.has(cacheKey)) {
      const cache = inlineLatexCache.get(cacheKey);

      // Move to end for LRU
      inlineLatexCache.delete(cacheKey);
      inlineLatexCache.set(cacheKey, cache);

      setLatexNode(cache);
      return;
    }

    renderLatex();
  }, [cacheKey, renderLatex, content]);

  return (
    <span
      data-render-pending={
        isStaticExport && !latexNode ? 'inline-equation' : undefined
      }
    >
      {latexNode ?? content}
    </span>
  );
}

const manageInlineCacheSize = () => {
  if (inlineLatexCache.size > INLINE_LATEX_CACHE_LIMIT) {
    const firstKey = inlineLatexCache.keys().next().value;
    if (firstKey) inlineLatexCache.delete(firstKey);
  }
};

type Kind = 'text' | 'inline' | 'block';
type Segment = { type: Kind; content: string };

function extractSegments(markdown: string): Segment[] {
  const tree = unified().use(remarkParse).use(remarkMath).parse(markdown);

  const segs: Segment[] = [];

  visit(tree, (node) => {
    if (node.type === 'inlineMath') {
      segs.push({ type: 'inline', content: node.value });
    } else if (node.type === 'math') {
      segs.push({ type: 'block', content: node.value });
    } else if (node.type === 'text') {
      // split text nodes to pull out bracketed display math like: [ \int ... ]
      segs.push(...splitBracketedDisplayMath(node.value));
    }
  });

  return coalesceText(segs);
}

/** split `[ ... ]` chunks that *look like* LaTeX into block segments */
function splitBracketedDisplayMath(text: string): Segment[] {
  const value = normalizeBlockMath(text);

  const out: Segment[] = [];
  const re = /\[\s*([\s\S]*?)\s*\]/g; // greedy enough to catch multiline blocks
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(value))) {
    const [full, inner] = m;

    // keep normal brackets (links, prose) intact
    if (!looksLikeLatex(inner)) continue;

    if (m.index > last)
      out.push({ type: 'text', content: value.slice(last, m.index) });
    out.push({ type: 'block', content: fixCommonSpacing(inner.trim()) });
    last = m.index + full.length;
  }

  if (last < value.length)
    out.push({ type: 'text', content: value.slice(last) });
  return out;
}

/**  Block/inline delimiters (avoid currency like $100) */
const BLOCK_DOLLAR_RE = /(?<!\\)\$\$[\s\S]+?\$\$/; // $$ ... $$
const BRACKET_BLOCK_RE = /\\\[[\s\S]+?\\\]/; // \[ ... \]
const PAREN_INLINE_RE = /\\\([\s\S]+?\\\)/; // \( ... \)
const INLINE_DOLLAR_RE = /(?<!\\)\$(?!\s|\d)[\s\S]+?(?<!\\)\$/; // $ ... $, not $100
/** Common LaTeX commands (expanded set incl. partial/theta/delta & friends) */
const CMD_LIST = [
  'frac',
  'sqrt',
  'sum',
  'int',
  'prod',
  'lim',
  'log',
  'ln',
  'sin',
  'cos',
  'tan',
  'cot',
  'sec',
  'csc',
  'pm',
  'times',
  'cdot',
  'le',
  'ge',
  'neq',
  'approx',
  'sim',
  'to',
  'infty',
  'nabla',
  'alpha',
  'beta',
  'gamma',
  'delta',
  'epsilon',
  'varepsilon',
  'zeta',
  'eta',
  'theta',
  'vartheta',
  'iota',
  'kappa',
  'lambda',
  'mu',
  'nu',
  'xi',
  'omicron',
  'pi',
  'rho',
  'sigma',
  'tau',
  'upsilon',
  'phi',
  'varphi',
  'chi',
  'psi',
  'omega',
  'Gamma',
  'Delta',
  'Theta',
  'Lambda',
  'Xi',
  'Pi',
  'Sigma',
  'Upsilon',
  'Phi',
  'Psi',
  'Omega',
  'partial',
];
const LATEX_COMMAND_RE = new RegExp(`\\\\(?:${CMD_LIST.join('|')})\\b`);
/** Cheap mathy syntax: subscripts/superscripts with letters (e.g., a_i, x^2) **/
const MATH_SYM_RE = /[_^]/;
const LETTER_RE = /[A-Za-z]/;

function looksLikeLatex(s: string): boolean {
  if (!s) return false;

  // Quick reject: if no obvious markers at all
  if (!s.includes('\\') && !s.includes('$') && !MATH_SYM_RE.test(s))
    return false;

  // Strong signals: explicit math delimiters
  if (BLOCK_DOLLAR_RE.test(s)) return true;
  if (BRACKET_BLOCK_RE.test(s)) return true;
  if (PAREN_INLINE_RE.test(s)) return true;
  if (INLINE_DOLLAR_RE.test(s)) return true;

  // Known LaTeX commands (includes \partial, \theta, etc.)
  if (LATEX_COMMAND_RE.test(s)) return true;

  // Generic mathy syntax like a_i or x^2
  if (MATH_SYM_RE.test(s) && LETTER_RE.test(s)) return true;

  return false;
}

/** repair the common ",dx" → "\,dx" mistake (but don't double-escape) */
function fixCommonSpacing(inner: string): string {
  return inner.replace(/(^|[^\\]),dx\b/g, (_m, pre) => `${pre}\\,dx`);
}

const RE_BEGIN_ALIGN = /\\begin\{align\*?\}/g;
const RE_END_ALIGN = /\\end\{align\*?\}/g;
const RE_INLINE_BLOCK_WITH_NL = /^\$[\s\S]+\$$/;
const RE_ALIGN_MARKER_EQ = /&=/g;
const RE_ALIGN_MARKER = /&/g;

/**
 * Normalizes LaTeX for KaTeX display mode.
 * - Converts align env to \[...\]
 * - Upgrades multi-line $...$ to $$...$$
 * - Removes '&' alignment markers
 */

function normalizeBlockMath(text: string): string {
  let out = text.trim();

  // Remove align environments
  out = out.replace(RE_BEGIN_ALIGN, '\[').replace(RE_END_ALIGN, '\]');

  // If inline math has newlines, convert to block math
  if (RE_INLINE_BLOCK_WITH_NL.test(out) && out.includes('\n')) {
    out = out.replace(/^\$/, '$$\n').replace(/\$$/, '\n$$');
  }

  // Remove alignment markers like "&="
  out = out.replace(RE_ALIGN_MARKER_EQ, '=').replace(RE_ALIGN_MARKER, '');

  return out;
}

function coalesceText(segs: Segment[]): Segment[] {
  const out: Segment[] = [];
  for (const s of segs) {
    if (
      s.type === 'text' &&
      out.length &&
      out[out.length - 1].type === 'text'
    ) {
      out[out.length - 1].content += s.content;
    } else out.push(s);
  }
  return out;
}
